import { Component, Input, OnInit } from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import moment from 'moment-timezone';

import { Holiday, WorkingHours } from 'src/app/constants';
import { MessageService } from 'src/services/message.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { DatetimeModalComponent } from '../datetime-modal/datetime-modal.component';

const MINUTES_PER_DAY = 60 * 24;
const DAYS_OF_WEEK = [
  {
    index: 0,
    display: 'Monday',
    min: MINUTES_PER_DAY * 0,
    max: MINUTES_PER_DAY * 1,
    startInterval: MINUTES_PER_DAY * 0,
    endInterval: MINUTES_PER_DAY * 1,
  },
  {
    index: 1,
    display: 'Tuesday',
    min: MINUTES_PER_DAY * 1,
    max: MINUTES_PER_DAY * 2,
    startInterval: MINUTES_PER_DAY * 1,
    endInterval: MINUTES_PER_DAY * 2,
  },
  {
    index: 2,
    display: 'Wednesday',
    min: MINUTES_PER_DAY * 2,
    max: MINUTES_PER_DAY * 3,
    startInterval: MINUTES_PER_DAY * 2,
    endInterval: MINUTES_PER_DAY * 3,
  },
  {
    index: 3,
    display: 'Thursday',
    min: MINUTES_PER_DAY * 3,
    max: MINUTES_PER_DAY * 4,
    startInterval: MINUTES_PER_DAY * 3,
    endInterval: MINUTES_PER_DAY * 4,
  },
  {
    index: 4,
    display: 'Friday',
    min: MINUTES_PER_DAY * 4,
    max: MINUTES_PER_DAY * 5,
    startInterval: MINUTES_PER_DAY * 4,
    endInterval: MINUTES_PER_DAY * 5,
  },
  {
    index: 5,
    display: 'Saturday',
    min: MINUTES_PER_DAY * 5,
    max: MINUTES_PER_DAY * 6,
    startInterval: MINUTES_PER_DAY * 5,
    endInterval: MINUTES_PER_DAY * 6,
  },
  {
    index: 6,
    display: 'Sunday',
    min: MINUTES_PER_DAY * 6,
    max: MINUTES_PER_DAY * 7,
    startInterval: MINUTES_PER_DAY * 6,
    endInterval: MINUTES_PER_DAY * 7,
  },
];

function sortIntervals(intervals: [number, number][]) {
  return intervals.sort((a, b) => {
    if (a[0] > b[0]) {
      return 1;
    } else if (a[0] < b[0]) {
      return -1;
    } else {
      return 0;
    }
  });
}

function mergeIntervals(intervals: [number, number][]) {
  const result: [number, number][] = [];

  let previousMin;
  let previousMax;

  for (const [min, max] of sortIntervals(intervals)) {
    if (previousMin === undefined) {
      previousMin = min;
      previousMax = max;
    } else if (min > previousMax) {
      result.push([previousMin, previousMax]);

      previousMin = min;
      previousMax = max;
    } else if (max > previousMax) {
      previousMax = max;
    }
  }

  result.push([previousMin, previousMax]);

  return result;
}

@Component({
  selector: 'app-working-hours',
  templateUrl: './working-hours.component.html',
  styleUrls: ['./working-hours.component.css'],
})
export class WorkingHoursComponent implements OnInit {
  @Input() originalWorkingHours: WorkingHours;
  @Input() onSave: (update: any) => Promise<any>;
  workingHours: WorkingHours = { enabled: false, intervals: [] };

  timesOfDay: any[] = [];
  daysOfWeek: any[] = [];

  timezoneOptions = moment.tz.names();
  timezone: string;

  isLoading: boolean;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private msgService: MessageService,
  ) {}

  ngOnInit() {
    const { enabled = false, intervals = [], timezone, holidays = [] } = this.originalWorkingHours ?? {};
    this.workingHours = {
      timezone,
      enabled,
      intervals,
      holidays,
    };

    if (intervals.length) {
      this.setDaysOfWeekForIntervals();
    } else {
      this.setDefaultWorkingHours();
    }

    this.timezone = timezone ?? 'UTC';

    const isAmPm: boolean = moment().format('LT').length > 5;
    const minutes = ['00', '30'];

    let suffix = isAmPm ? ' AM' : '';

    for (let h = 0; h <= 23; h++) {
      if (isAmPm && h >= 12) {
        suffix = ' PM';
      }

      let hour = h || (isAmPm ? 12 : 0);

      if (isAmPm && hour > 12) {
        hour = hour - 12;
      }

      for (const m of minutes) {
        this.timesOfDay.push({
          id: `${hour}-${m}-${suffix.replace(' ', '').toLowerCase()}`,
          display: `${hour}:${m}${suffix}`,
          value: Number(`${h}${m}`),
          minutes: Number(h * 60) + Number(m),
        });
      }
    }

    this.timesOfDay.push({
      id: 'end',
      display: `End of day`,
      value: 2400,
      minutes: MINUTES_PER_DAY,
    });

    if (intervals.length) {
      this.setDaysOfWeekForIntervals();
    } else {
      this.setDefaultWorkingHours();
    }
  }

  setDefaultWorkingHours() {
    this.daysOfWeek = DAYS_OF_WEEK.map((day) => {
      return {
        ...day,
        startInterval: day.min + 9 * 60,
        endInterval: day.min + 17 * 60,
      };
    });
  }

  setDaysOfWeekForIntervals() {
    this.daysOfWeek = [];

    const intervals = mergeIntervals(
      this.workingHours.intervals.reduce((acc, next) => {
        acc.push([next['0'], next['1']]);
        return acc;
      }, []),
    );

    intervals.map((interval) => {
      const start = interval['0'];
      const end = interval['1'];
      const startIndex = Math.floor(start / MINUTES_PER_DAY);
      const startDay = { ...DAYS_OF_WEEK[startIndex] };
      const endIndex = end === startDay.max ? startIndex : Math.floor(end / MINUTES_PER_DAY);

      startDay.startInterval = start;

      if (startIndex === endIndex) {
        startDay.endInterval = end;

        // Temporary workaround:
        // If you set a time from Monday 9:00am to 1:00am, we'll assume 9am is the following day
        if (startDay.endInterval > startDay.max) {
          startDay.endInterval = startDay.endInterval - MINUTES_PER_DAY;
        }

        this.daysOfWeek.push({ ...startDay });
      } else {
        startDay.endInterval = startDay.max;

        this.daysOfWeek.push({ ...startDay });

        for (let i = startIndex + 1; i < endIndex; i++) {
          const innerDay = { ...DAYS_OF_WEEK[i] };

          innerDay.startInterval = innerDay.min;
          innerDay.endInterval = innerDay.max;

          this.daysOfWeek.push({ ...innerDay });
        }

        const endDay = { ...DAYS_OF_WEEK[endIndex] };

        endDay.startInterval = endDay.min;
        endDay.endInterval = end;

        if (endDay.endInterval > endDay.startInterval) {
          this.daysOfWeek.push({ ...endDay });
        }
      }
    });
  }

  setIntervalsForDaysOfWeek() {
    const intervals = this.daysOfWeek.reduce((acc, next) => {
      if (next.startInterval < next.endInterval) {
        acc.push([next.startInterval, next.endInterval]);
      } else {
        // Temporary workaround:
        // If you set a time from Monday 9:00am to 1:00am, we'll assume 9am is the following day
        acc.push([next.startInterval, next.endInterval + MINUTES_PER_DAY]);
      }

      return acc;
    }, []);

    this.workingHours.intervals = intervals.length
      ? mergeIntervals(intervals).map((i) => ({
          0: i[0],
          1: i[1],
        }))
      : [];
  }

  deleteOperationHours(index: number) {
    this.daysOfWeek.splice(index, 1);
  }

  async addOperationHours() {
    const options: ActionDropdownType[] = DAYS_OF_WEEK.map((day) => {
      return { label: day.display };
    });
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (index: number) => {
          popover.dismiss();

          this.daysOfWeek.push({ ...DAYS_OF_WEEK[index] });
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }

  addHoliday() {
    if (!this.workingHours.holidays) {
      this.workingHours.holidays = [];
    }

    this.workingHours.holidays.push({
      name: '',
      startDate: '',
      endDate: '',
    });
  }

  deleteHoliday(index: number) {
    this.workingHours.holidays.splice(index, 1);
  }

  async save() {
    this.setIntervalsForDaysOfWeek();
    this.workingHours.timezone = this.timezone;

    this.isLoading = true;

    const updates: any = {
      workingHours: this.workingHours,
    };

    try {
      await this.onSave(updates);
      this.isLoading = false;
      this.msgService.showBottom('✔︎ Saved');
      this.dismiss();
    } catch (err) {
      this.isLoading = false;
      this.msgService.showBottom(`Whoops! An error occurred: ${err.message}`);
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  async setHolidayDate(holiday: any, type: 'startDate' | 'endDate') {
    const presentingElement = await this.modalCtrl.getTop();

    presentingElement.canDismiss = false;

    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: holiday[type],
        title: `Select ${type}`,
      },
      presentingElement,
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then(() => {
      presentingElement.canDismiss = true;
    });

    const { data } = await datePickerModal.onDidDismiss();
    const result = data?.value;

    if (result && result !== 'Invalid date') {
      holiday[type] = result;
    }
  }
}
